import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import EmotionDetector from './components/EmotionDetector';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import EmailVerified from './components/EmailVerified';
import VerifyEmailPending from './components/VerifyEmailPending';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import About from './components/About';
import TechDocs from './components/TechDocs';
import ApiKeys from './components/ApiKeys';
import Admin from './components/Admin';
import SubscriptionPlans from './components/SubscriptionPlans';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionCancel from './components/SubscriptionCancel';
import { getCurrentUser } from './api/client';


interface NavigationProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  onLogout: () => void;
}

function Navigation({ isAuthenticated, isAdmin, onLogout }: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isAdminUser = isAuthenticated && isAdmin;

  const isActive = (path: string) => location.pathname === path;

  if (isAdminUser) {
    return (
      <nav className="bg-slate-950/90 backdrop-blur-sm shadow-lg border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-slate-800 border border-indigo-500/40 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex items-center">
                <span className="text-xl font-bold text-indigo-100">EmotionWise</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]">
                Admin
              </span>
              <button
                onClick={onLogout}
                className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-indigo-300 transition-colors duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-slate-950/85 backdrop-blur-sm shadow-lg border-b border-slate-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-slate-800 border border-indigo-500/40 rounded-lg flex items-center justify-center">
                <svg className="h-5 w-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex items-center">
                <span className="text-xl font-bold text-indigo-100">EmotionWise</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {!isAdminUser && (
              <Link
                to="/"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive('/') 
                    ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]' 
                    : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                }`}
              >
                Emotion Detector
              </Link>
            )}
            {isAuthenticated && !isAdminUser && (
              <Link
                to="/api-keys"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive('/api-keys')
                    ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]'
                    : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                }`}
              >
                API Keys
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive('/admin') 
                    ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]' 
                    : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                }`}
              >
                Admin
              </Link>
            )}
          </div>

          {/* Auth & Secondary Actions */}
          <div className="hidden md:flex items-center space-x-3">
            {!isAdminUser && (
              <Link
                to="/tech-docs"
                className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200"
              >
                Docs
              </Link>
            )}
            {!isAdminUser && (
              <a
                href="https://www.emotionwise.social/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200"
              >
                EmotionWise Social
              </a>
            )}

            {!isAdminUser && (
              <Link
                to="/subscription_plans"
                className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors duration-200 ${
                  isActive('/subscription_plans')
                    ? 'border-indigo-400/50 bg-indigo-500/15 text-indigo-200'
                    : 'border-indigo-500/40 text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/15'
                }`}
              >
                Pricing
              </Link>
            )}
            {isAuthenticated ? (
              <button
                onClick={onLogout}
                className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-indigo-300 transition-colors duration-200"
              >
                Sign Out
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-indigo-300 transition-colors duration-200"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-sm"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15 transition-all duration-200"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-slate-800 py-4 animate-in slide-in-from-top-2 duration-300">
            <div className="space-y-1">
              {!isAdminUser && (
                <Link
                  to="/"
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 mx-2 ${
                    isActive('/') 
                      ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]' 
                      : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Emotion Detector
                </Link>
              )}
              {isAuthenticated && !isAdminUser && (
                <Link
                  to="/api-keys"
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 mx-2 ${
                    isActive('/api-keys')
                      ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]'
                      : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  API Keys
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 mx-2 ${
                    isActive('/admin') 
                      ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]' 
                      : 'text-slate-300 hover:text-indigo-300 hover:bg-indigo-500/15'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin
                </Link>
              )}
              {!isAdminUser && (
                <Link
                  to="/tech-docs"
                  className="block px-4 py-3 text-base text-slate-400 hover:text-slate-200 transition-colors duration-200 mx-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Docs
                </Link>
              )}
              {!isAdminUser && (
                <a
                  href="https://www.emotionwise.social/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 text-base text-slate-400 hover:text-slate-200 transition-colors duration-200 mx-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  EmotionWise Social
                </a>
              )}

              {!isAdminUser && (
                <Link
                  to="/subscription_plans"
                  className={`block px-4 py-3 text-base font-medium rounded-lg border transition-colors duration-200 mx-2 ${
                    isActive('/subscription_plans')
                      ? 'border-indigo-400/50 bg-indigo-500/15 text-indigo-200'
                      : 'border-indigo-500/40 text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/15'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pricing
                </Link>
              )}
              
              {/* Auth Section */}
              <div className="pt-4 mt-4 border-t border-slate-800 mx-2">
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      onLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-base font-medium text-slate-300 hover:text-indigo-300 transition-colors duration-200 rounded-lg hover:bg-slate-800"
                  >
                    Sign Out
                  </button>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      className="block px-4 py-3 text-base font-medium text-slate-300 hover:text-indigo-300 transition-colors duration-200 rounded-lg hover:bg-slate-800"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-base font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-sm"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950/80 backdrop-blur-sm border-t border-slate-800 mt-12">
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-base font-bold text-indigo-100">EmotionWise</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              BERT-based emotion &amp; sarcasm detection API. 28 labels, multilingual, production-ready.
            </p>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Product</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">Emotion Detector</Link></li>
              <li><Link to="/subscription_plans" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">Pricing</Link></li>
              <li><Link to="/tech-docs" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">API Docs</Link></li>
              <li><Link to="/api-keys" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">API Keys</Link></li>
              <li><a href="https://www.emotionwise.social/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">EmotionWise Social</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Company</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">About</Link></li>
              <li><Link to="/privacy" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">Terms of Service</Link></li>
              <li><a href="mailto:support@emotionwise.ai" className="text-sm text-slate-400 hover:text-slate-200 transition-colors duration-200">Contact</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Get Started</h3>
            <Link
              to="/register"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
            >
              Free API Key
            </Link>
            <p className="text-xs text-slate-500 mt-2">1,000 calls/month. No card required.</p>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-6 text-center">
          <p className="text-xs text-slate-500">
            © 2026 EmotionWise. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const isAdminUser = isAuthenticated && isAdmin === true;
  const navigate = useNavigate();

  useEffect(() => {
    const refreshAuthState = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setIsAdmin(false);
        setCurrentPlan(null);
        return;
      }

      setIsAuthenticated(true);
      setIsAdmin(null);

      try {
        const response = await getCurrentUser();
        setIsAdmin(response.data?.role_id === 2);
        setCurrentPlan((response.data?.plan_name || 'free').toLowerCase());
      } catch {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setIsAdmin(false);
        setCurrentPlan(null);
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        void refreshAuthState();
      }
    };

    const handleAuthStateChange = () => {
      void refreshAuthState();
    };

    void refreshAuthState();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthStateChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthStateChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setCurrentPlan(null);
    window.dispatchEvent(new CustomEvent('authStateChanged'));
    navigate('/', { replace: true });
  };

  return (
    <div className="ew-theme min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex flex-col">
      <Navigation
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin === true}
        onLogout={handleLogout}
      />
      <main className="flex-grow">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated && isAdmin === null ? (
                <div className="max-w-3xl mx-auto py-12 px-4 text-center text-slate-300">
                  Loading...
                </div>
              ) : isAuthenticated && isAdmin === true ? (
                <Navigate to="/admin" replace />
              ) : (
                <EmotionDetector isAuthenticated={isAuthenticated} currentPlan={currentPlan} />
              )
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated && isAdmin === true ? (
                <Navigate to="/admin" replace />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated && isAdmin === true ? (
                <Navigate to="/admin" replace />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Register />
              )
            }
          />
          <Route
            path="/verify-email-pending"
            element={
              isAuthenticated && isAdmin === true ? (
                <Navigate to="/admin" replace />
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <VerifyEmailPending />
              )
            }
          />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="/privacy" element={isAdminUser ? <Navigate to="/admin" replace /> : <Privacy />} />
          <Route path="/terms" element={isAdminUser ? <Navigate to="/admin" replace /> : <Terms />} />
          <Route path="/forgot-password" element={isAdminUser ? <Navigate to="/admin" replace /> : isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />} />
          <Route path="/reset-password" element={isAdminUser ? <Navigate to="/admin" replace /> : isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />} />
          <Route path="/about" element={isAdminUser ? <Navigate to="/admin" replace /> : <About />} />
          <Route path="/tech-docs" element={isAdminUser ? <Navigate to="/admin" replace /> : <TechDocs />} />
          <Route path="/subscription_plans" element={isAdminUser ? <Navigate to="/admin" replace /> : <SubscriptionPlans />} />
          <Route path="/subscription_success" element={isAdminUser ? <Navigate to="/admin" replace /> : <SubscriptionSuccess />} />
          <Route path="/subscription_cancel" element={isAdminUser ? <Navigate to="/admin" replace /> : <SubscriptionCancel />} />
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                {isAdminUser ? <Navigate to="/admin" replace /> : <ApiKeys />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                {isAdmin === null ? (
                  <div className="max-w-3xl mx-auto py-12 px-4 text-center text-slate-300">
                    Loading admin access...
                  </div>
                ) : isAdmin ? (
                  <Admin />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={<Navigate to={isAuthenticated && isAdmin ? "/admin" : "/"} replace />}
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
